'use client';

import { useCallback, useId, useRef } from 'react';
import type { ReactNode } from 'react';

import { Dialog } from '@base-ui/react/dialog';
import { IconChevronRight, IconHome } from '@tabler/icons-react';

import {
  ACCOUNT_ICON,
  AccountIdentity,
  AccountLoading,
  AccountRetry,
  HELP_ICON,
  MenuItemContent,
  SIGN_OUT_ICON,
  SignInLink,
  TransactionIcon,
  UnavailableItemContent,
} from './header-account';
import { HeaderAnchor } from './header-link';
import { HeaderLogo } from './logo';
import { HEADER_OVERLAY_LAYER } from './model';
import type {
  HeaderAccountMenu,
  HeaderAccountState,
  HeaderCommandAction,
  HeaderDrawerCallToAction,
  HeaderIdentity,
  HeaderLinkAction,
  HeaderNavItem,
  HeaderUnavailableAction,
} from './model';

const NAV_ROW_CLASS =
  'flex w-full items-center border-b border-od-border-secondary py-4 text-left text-lg leading-[1.45] tracking-[-0.024em] text-od-content-primary no-underline last:border-b-0';

const ACCOUNT_ROW_CLASS =
  'flex min-h-12 w-full cursor-pointer items-center gap-2 rounded-lg border-0 bg-transparent py-3 pr-1 text-left text-base leading-6 tracking-[-0.014em] text-od-content-primary no-underline hover:bg-od-bg-hover-primary aria-disabled:cursor-default aria-disabled:hover:bg-transparent aria-[current=page]:font-medium';

type DrawerControls = {
  close: () => void;
  runCommand: (command: HeaderCommandAction) => void;
};

function DrawerNavigation({ items, close }: { items: HeaderNavItem[]; close: () => void }) {
  if (items.length === 0) return null;
  return (
    <nav aria-label="Primary mobile" className="px-2 pt-2">
      {items.map((item) =>
        item.kind === 'link' ? (
          <HeaderAnchor
            key={item.key}
            action={item.action}
            placement="drawer"
            aria-current={item.current ? 'page' : undefined}
            className={NAV_ROW_CLASS}
            onClick={close}
          >
            {item.label}
          </HeaderAnchor>
        ) : (
          <div key={item.key} className="py-2">
            <p className="m-0 pt-2 pb-1 text-sm font-medium text-od-content-tertiary">
              {item.label}
            </p>
            {item.links.map((link) => (
              <HeaderAnchor
                key={link.key}
                action={link.action}
                placement="drawer"
                aria-current={link.current ? 'page' : undefined}
                className={NAV_ROW_CLASS}
                onClick={close}
              >
                {link.label}
              </HeaderAnchor>
            ))}
          </div>
        ),
      )}
    </nav>
  );
}

function DrawerLinkRow({
  action,
  icon,
  current = false,
  close,
  children,
}: {
  action: HeaderLinkAction;
  icon: ReactNode;
  current?: boolean;
  close: () => void;
  children: ReactNode;
}) {
  return (
    <HeaderAnchor
      action={action}
      placement="drawer"
      aria-current={current ? 'page' : undefined}
      className={ACCOUNT_ROW_CLASS}
      onClick={close}
    >
      <MenuItemContent icon={icon}>{children}</MenuItemContent>
    </HeaderAnchor>
  );
}

function DrawerCommandRow({
  command,
  label,
  icon,
  runCommand,
}: {
  command: HeaderCommandAction | HeaderUnavailableAction;
  label: string;
  icon: ReactNode;
  runCommand: DrawerControls['runCommand'];
}) {
  const reasonId = useId();
  const handleClick = useCallback(() => {
    if (command.kind === 'command') runCommand(command);
  }, [command, runCommand]);
  if (command.kind === 'unavailable') {
    // aria-disabled keeps the control and its reason reachable from the keyboard.
    return (
      <button
        type="button"
        className={ACCOUNT_ROW_CLASS}
        aria-disabled="true"
        aria-label={label}
        aria-describedby={reasonId}
      >
        <UnavailableItemContent
          icon={icon}
          label={label}
          reason={command.reason}
          reasonId={reasonId}
        />
      </button>
    );
  }
  return (
    <button type="button" className={ACCOUNT_ROW_CLASS} onClick={handleClick}>
      <MenuItemContent icon={icon}>{label}</MenuItemContent>
    </button>
  );
}

function DrawerAccountMenu({
  identity,
  menu,
  close,
  runCommand,
}: { identity: HeaderIdentity; menu: HeaderAccountMenu } & DrawerControls) {
  return (
    <div className="pt-3">
      <div className="rounded-2xl border border-od-bg-hover-primary bg-od-bg-secondary p-4 pb-2">
        <div className="pb-2">
          <AccountIdentity identity={identity} placement="drawer" />
        </div>
        <DrawerLinkRow
          action={menu.dashboard.action}
          icon={<IconHome size={20} stroke={1.5} aria-hidden />}
          current={menu.dashboard.current}
          close={close}
        >
          Dashboard
        </DrawerLinkRow>
        {menu.transactions.map((row) => (
          <DrawerLinkRow
            key={row.key}
            action={row.action}
            icon={<TransactionIcon icon={row.icon} />}
            close={close}
          >
            {row.label}
          </DrawerLinkRow>
        ))}
        {menu.transactionLinks.map((link) => (
          <DrawerLinkRow key={link.key} action={link.action} icon={null} close={close}>
            {link.label}
          </DrawerLinkRow>
        ))}
        <div aria-hidden className="py-2">
          <div className="h-px bg-od-bg-hover-primary" />
        </div>
        <DrawerLinkRow action={menu.account} icon={ACCOUNT_ICON} close={close}>
          Account
        </DrawerLinkRow>
        <div aria-hidden className="py-2">
          <div className="h-px bg-od-border-secondary" />
        </div>
        <DrawerLinkRow action={menu.helpCenter} icon={HELP_ICON} close={close}>
          Help center
        </DrawerLinkRow>
        <DrawerCommandRow
          command={menu.signOut}
          label="Sign out"
          icon={SIGN_OUT_ICON}
          runCommand={runCommand}
        />
      </div>
    </div>
  );
}

function DrawerAccountState({
  state,
  focusAfterRetry,
  ...controls
}: { state: HeaderAccountState; focusAfterRetry: () => void } & DrawerControls) {
  if (state.status === 'loading') {
    return (
      <div className="px-2 pt-5">
        <AccountLoading />
      </div>
    );
  }
  if (state.status === 'authenticated') {
    return <DrawerAccountMenu identity={state.identity} menu={state.menu} {...controls} />;
  }
  const retry = state.status === 'error' ? state.retry : null;
  if (!state.signIn && !retry) return null;
  return (
    <div className="flex items-center gap-2 px-2 pt-5">
      {retry ? (
        <AccountRetry retry={retry} placement="drawer" focusAfterRetry={focusAfterRetry} />
      ) : null}
      {state.signIn ? (
        <SignInLink signIn={state.signIn} placement="drawer" onSelect={controls.close} />
      ) : null}
    </div>
  );
}

function DrawerAccount(props: { state: HeaderAccountState } & DrawerControls) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const focusSection = useCallback(() => sectionRef.current?.focus(), []);
  return (
    <div ref={sectionRef} tabIndex={-1} className="outline-none">
      <DrawerAccountState {...props} focusAfterRetry={focusSection} />
    </div>
  );
}

function DrawerCallToAction({
  callToAction,
  close,
}: {
  callToAction: HeaderDrawerCallToAction;
  close: () => void;
}) {
  return (
    <div className="px-2 pt-4">
      <HeaderAnchor
        action={callToAction.action}
        placement="drawer"
        className="flex items-center justify-between rounded-2xl bg-od-bg-secondary px-5 py-4 no-underline"
        onClick={close}
      >
        <span className="flex flex-col">
          <span className="text-sm font-medium text-od-content-primary">{callToAction.title}</span>{' '}
          <span className="text-sm text-od-content-secondary">{callToAction.label}</span>
        </span>
        <IconChevronRight size={24} aria-hidden className="text-od-content-primary" />
      </HeaderAnchor>
    </div>
  );
}

export function MobileNav({
  home,
  navigation,
  account,
  callToAction,
  open,
  setOpen,
}: {
  home: HeaderLinkAction;
  navigation: HeaderNavItem[];
  account: HeaderAccountState;
  callToAction: HeaderDrawerCallToAction | null;
  open: boolean;
  setOpen: (open: boolean) => void;
}) {
  const popupRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), [setOpen]);
  const runCommand = useCallback(
    (command: HeaderCommandAction) => {
      close();
      command.run();
    },
    [close],
  );
  const restoreFocus = useCallback(() => {
    // A customer who closed the drawer by focusing a page control, such as a search field drawn
    // over the header, keeps that focus.
    const focused = document.activeElement;
    return (
      focused === null || focused === document.body || Boolean(popupRef.current?.contains(focused))
    );
  }, []);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger
        aria-label="Open navigation menu"
        className="inline-flex size-10 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0 text-current transition hover:bg-od-bg-hover-primary focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-od-border-focused-accent-brand site-header-desktop:hidden group-data-[appearance=inverse]/site-header:hover:bg-white/12 group-data-[appearance=inverse]/site-header:focus-visible:outline-od-content-inverse-primary"
      >
        <svg aria-hidden width="22" height="22" viewBox="0 0 22 22" fill="none">
          <path
            d="M3 6h16M3 11h16M3 16h16"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Backdrop
          className={`fixed inset-0 ${HEADER_OVERLAY_LAYER} bg-od-bg-scrim/20 transition-opacity duration-300 data-[ending-style]:opacity-0 data-[starting-style]:opacity-0`}
        />
        <Dialog.Popup
          ref={popupRef}
          finalFocus={restoreFocus}
          className={`fixed inset-x-0 top-0 ${HEADER_OVERLAY_LAYER} max-h-[100dvh] origin-top overflow-y-auto rounded-b-[24px] bg-od-bg-primary px-3 pt-[calc(--spacing(4)+env(safe-area-inset-top))] pb-[calc(--spacing(3)+env(safe-area-inset-bottom))] shadow-lg outline-none transition duration-200 data-[ending-style]:opacity-0 data-[starting-style]:opacity-0`}
        >
          <Dialog.Title className="sr-only">Menu</Dialog.Title>
          <div className="flex items-center justify-between px-2">
            <HeaderLogo action={home} placement="drawer" onClick={close} />
            <Dialog.Close
              aria-label="Close menu"
              className="inline-flex size-11 cursor-pointer items-center justify-center rounded-[10px] border-0 bg-od-bg-tertiary p-0 text-od-content-primary transition hover:bg-od-bg-hover-primary focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-od-border-focused-accent-brand"
            >
              <svg aria-hidden width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M4 4l8 8M12 4L4 12"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </Dialog.Close>
          </div>
          <DrawerNavigation items={navigation} close={close} />
          <DrawerAccount state={account} close={close} runCommand={runCommand} />
          {callToAction ? <DrawerCallToAction callToAction={callToAction} close={close} /> : null}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
